import styled from "styled-components";
import { getTheme, themed, InteractiveElement, Strut, Text as Tx, } from "@bracket/ui";
import { useNavigate, } from "react-router-dom";


export declare type TabItem<T extends string|number = string> = {
	id: T;
	label: string;
	route?: `/${string}`;
	defaultRoute?: boolean;
	dev?: boolean;
};

export declare type TabsProps<T extends string|number = string> = {
	items: TabItem<T>[];
	activeTab: T;
	onTabChange?: (tabId: T) => void;
	route?: `/${string}`;
};

export const Tabs = <T extends string|number = string>({
	items,
	activeTab,
	onTabChange = () => {},
	route,
}: TabsProps<T>): React.ReactElement => {
	themed();

	const navigate = useNavigate();

	const onChange = (tabId: T) => {
		const item = items.find((item) => item.id === tabId);
		if(!item)
			throw new Error(`Tab item with id "${tabId}" not found`);
		const routeTo = route || item.route;
		onTabChange(tabId);
		if(routeTo) {
			navigate(routeTo);
		}
	};

	const isDevRenderable = (item: TabItem<T>): boolean => !item.dev || !globalThis.location.hostname.startsWith("app.bracket");

	return (
		<TabsContainer>
			{items.map((item) => (
				isDevRenderable(item) ? (
					<TabButton key={item.id} $active={item.id === activeTab} onClick={() => onChange(item.id)}>
						<Tx color="inherit">{item.label}</Tx>
					</TabButton>
				)
				: null
			))}
		</TabsContainer>
	);
};


const TabsContainer = styled(Strut).attrs({ $desktopFlow: "stream", })`
	border-bottom: ${() => `1px solid ${getTheme().outline}`};
	gap: 0.5rem;
`;


const TabButton = styled(InteractiveElement)<{ $active: boolean }>`
	padding: 1rem 1.25rem;
	color: ${({ $active }) => $active ? getTheme().primary_variant : getTheme().subheading};
	${({ $active, }) => $active && `border-bottom: 1px solid ${getTheme().primary};`}
	${({ $active }) => $active && `background: ${getTheme().gradients.secondary};`}
	white-space: nowrap;
	transition: color 0.3s ease;
	&:hover {
		color: ${() => getTheme().primary_fixed_dim};
	}
`;
